/**
 * Canvas-animated favicon: the DDiskSatellite mark (a satellite orbiting a disk)
 * redrawn each frame onto a small canvas whose data-URL is pushed to
 * <link rel="icon">. SVG/GIF favicons don't animate in browser tabs, so we drive
 * it frame-by-frame. Throttled to ~13 fps, paused when the tab is hidden, and a
 * single static frame under prefers-reduced-motion. Mirrors `components/Logo.tsx`.
 */

const SIZE = 64; // favicon canvas is 64×64 (browser downscales to 16/32)
const SCALE = SIZE / 48; // Logo geometry is authored on a 48×48 viewBox
const CENTER = 24 * SCALE;
const DISK_R = 10 * SCALE;
const HUB_R = 3.4 * SCALE;
const ORBIT_RX = 21 * SCALE;
const ORBIT_RY = 9 * SCALE;
const TILT = (-28 * Math.PI) / 180;
const SAT_R = 3 * SCALE;
const HALO_R = 5.5 * SCALE;
const FRAME_MS = 1000 / 13; // ~13 fps
const PERIOD_MS = 9000; // one full orbit, matching the SVG's 9s

/** Point on the tilted orbit ellipse at parametric angle θ. */
function orbitPoint(theta: number): { x: number; y: number } {
  const dx = ORBIT_RX * Math.cos(theta);
  const dy = ORBIT_RY * Math.sin(theta);
  return {
    x: CENTER + dx * Math.cos(TILT) - dy * Math.sin(TILT),
    y: CENTER + dx * Math.sin(TILT) + dy * Math.cos(TILT),
  };
}

function draw(ctx: CanvasRenderingContext2D, theta: number): void {
  ctx.clearRect(0, 0, SIZE, SIZE);

  // Orbit ring (faint).
  ctx.save();
  ctx.translate(CENTER, CENTER);
  ctx.rotate(TILT);
  ctx.beginPath();
  ctx.ellipse(0, 0, ORBIT_RX, ORBIT_RY, 0, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(91,141,239,0.55)';
  ctx.lineWidth = 2 * SCALE;
  ctx.stroke();
  ctx.restore();

  // Disk (brand gradient) + dark hub.
  const grad = ctx.createLinearGradient(CENTER - DISK_R, CENTER - DISK_R, CENTER + DISK_R, CENTER + DISK_R);
  grad.addColorStop(0, '#5b8def');
  grad.addColorStop(1, '#7c5cff');
  ctx.beginPath();
  ctx.arc(CENTER, CENTER, DISK_R, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(CENTER, CENTER, HUB_R, 0, Math.PI * 2);
  ctx.fillStyle = '#0d1220';
  ctx.fill();

  // Satellite: soft halo + dot, riding the orbit.
  const p = orbitPoint(theta);
  ctx.beginPath();
  ctx.arc(p.x, p.y, HALO_R, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(124,92,255,0.16)';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(p.x, p.y, SAT_R, 0, Math.PI * 2);
  ctx.fillStyle = '#7c5cff';
  ctx.fill();
}

/** Ensure a single <link rel="icon"> we control, and return it. */
function iconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link#ddisk-favicon');
  if (!link) {
    link = document.createElement('link');
    link.id = 'ddisk-favicon';
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  // Drop any other icon links (e.g. the static logo.svg) so ours wins.
  document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]').forEach((l) => {
    if (l !== link) l.remove();
  });
  return link;
}

/** Start the animated favicon. Safe to call once at app boot. */
export function startAnimatedFavicon(): void {
  if (typeof document === 'undefined') return;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const link = iconLink();

  const push = (theta: number): void => {
    draw(ctx, theta);
    link.type = 'image/png';
    link.href = canvas.toDataURL('image/png');
  };

  const reduce =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) {
    // Static frame with the satellite parked at the top-right, like the SVG.
    push(0);
    return;
  }

  // Time-based angle so throttling/pausing never changes orbit speed. We can't
  // call Date.now() at module load in some sandboxes, but in a browser at
  // runtime it's fine; performance.now() avoids wall-clock entirely.
  const t0 = performance.now();
  let last = 0;
  let raf = 0;
  const tick = (now: number): void => {
    if (now - last >= FRAME_MS) {
      last = now;
      const theta = ((now - t0) / PERIOD_MS) * Math.PI * 2;
      push(theta);
    }
    raf = requestAnimationFrame(tick);
  };

  const start = (): void => {
    if (!raf) raf = requestAnimationFrame(tick);
  };
  const stop = (): void => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  };

  // rAF already halts on a hidden tab, but be explicit so we never spin.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else start();
  });
  start();
}
